import { supabase } from '@/integrations/supabase/client';
import { Sentry } from '@/lib/sentry';
import { toast } from 'sonner';

type ErrorSeverity = 'info' | 'warning' | 'error' | 'fatal';

interface ErrorReportContext {
  area?: string;
  action?: string;
  severity?: ErrorSeverity;
  metadata?: Record<string, unknown>;
  /** Plain-English description of what the user was doing, shown to them in
   *  the acknowledgement email instead of a raw stack-trace message. */
  userMessage?: string;
}

function normaliseError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    name: 'Error',
    message: typeof error === 'string' ? error : JSON.stringify(error),
    stack: undefined,
  };
}

function shouldRateLimit(fingerprint: string): boolean {
  try {
    const key = `medd-error-report:${fingerprint}`;
    const lastSent = Number(sessionStorage.getItem(key) || 0);
    const now = Date.now();

    if (lastSent && now - lastSent < 60_000) {
      return true;
    }

    sessionStorage.setItem(key, String(now));
  } catch {
    // sessionStorage can be blocked; reporting should still continue.
  }

  return false;
}

export async function reportClientError(
  error: unknown,
  context: ErrorReportContext = {},
): Promise<{ reference: string | null }> {
  const normalised = normaliseError(error);
  const severity = context.severity || 'error';
  const fingerprint = `${context.area || 'app'}:${context.action || 'unknown'}:${normalised.message}`;

  Sentry.withScope((scope) => {
    scope.setTag('flow', 'client_error_report');
    if (context.area) scope.setTag('area', context.area);
    if (context.action) scope.setTag('action', context.action);
    scope.setLevel(severity === 'fatal' ? 'fatal' : severity === 'warning' ? 'warning' : 'error');
    scope.setContext('medd_error_context', context.metadata || {});
    Sentry.captureException(error instanceof Error ? error : new Error(normalised.message));
  });

  if (shouldRateLimit(fingerprint)) {
    return { reference: null };
  }

  try {
    const { data, error: reportError } = await supabase.functions.invoke('report-client-error', {
      body: {
        error: normalised,
        context: {
          ...context,
          severity,
          userMessage: context.userMessage,
          url: window.location.href,
          pathname: window.location.pathname,
          userAgent: window.navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      },
    });

    if (reportError) throw reportError;

    const reference: string | null = data?.reference || null;

    if (data?.success && !data?.rateLimited && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('medd:error-reported', {
        detail: {
          area: context.area || 'app',
          action: context.action || 'unknown',
          severity,
          reference,
          // When the caller shows its own specific toast (reportUserFacingError),
          // suppress the generic acknowledgement toast to avoid double-toasting.
          suppressAck: Boolean(context.metadata?._suppressAckToast),
        },
      }));
    }

    return { reference };
  } catch (reportError) {
    // Never let reporting failures create a second user-facing error.
    console.warn('[ErrorReporter] Failed to report client error:', reportError);
    return { reference: null };
  }
}

/**
 * Report an error AND show the user a specific, reassuring toast in one call.
 * Use this in catch blocks instead of a bare toast.error('Something went
 * wrong') — the user sees what failed, that the tech team is notified, and a
 * reference code they can quote. The technical detail goes only to
 * notifications@pacificwavedigital.com (never the tenant's own admins), and a
 * reassurance email goes to the affected user.
 *
 * @param userMessage plain-English "we couldn't save the agent" style message
 */
export async function reportUserFacingError(
  error: unknown,
  opts: {
    userMessage: string;
    area?: string;
    action?: string;
    severity?: ErrorSeverity;
    metadata?: Record<string, unknown>;
  },
): Promise<void> {
  const { reference } = await reportClientError(error, {
    area: opts.area,
    action: opts.action,
    severity: opts.severity || 'error',
    metadata: { ...opts.metadata, _suppressAckToast: true },
    userMessage: opts.userMessage,
  });

  toast.error(opts.userMessage, {
    description: reference
      ? `Our tech team has been notified (ref ${reference}). Please try again shortly.`
      : 'Our tech team has been notified. Please try again shortly.',
    duration: 8000,
  });
}
